import React, { useEffect, useRef, useState } from 'react'
import {
  PlotContainer,
  ChartContainer,
  ChartTitle,
  ChartLegend,
  LegendItem,
  LegendLine,
  ErrorContainer,
  ErrorTitle,
  ErrorDescription,
} from './styles/BuildScenarioPlot.styles'
import {
  useLastRemoteModelOutput,
  useRemoteModelLoading,
  useInitialCleanModelOutput,
  useScenarioNameChanges,
  useGetInitialCleanModelOutput,
} from '../../stores/buildScenarioStore'
import useBuildScenarioStore from '../../stores/buildScenarioStore'
import { scenarioPlotD3 } from './utils/ScenarioPlot.d3'
import { RunButtonWithIconWrapper } from '../../../../models/build_model_page/build_model/styles/RunModel.styles'

const BuildScenarioPlot = () => {
  const lastOutput = useLastRemoteModelOutput()
  const isLoading = useRemoteModelLoading()
  const initialCleanOutput = useInitialCleanModelOutput()
  const getInitialCleanModelOutput = useGetInitialCleanModelOutput()
  const selectedModel = useBuildScenarioStore((state) => state.selectedModel)
  const [run, setRun] = useState(false)
  const abortControllerRef = useRef(null)

  const initialCleanModelError = useBuildScenarioStore(
    (state) => state.initialCleanModelError,
  )

  const handleRun = async () => {
    setRun(true)

    // Abort any ongoing request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }

    // Create a new AbortController
    abortControllerRef.current = new AbortController()
    const signal = abortControllerRef.current.signal

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/artifacts/predict/impact`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
          body: JSON.stringify({
            mode: 'rerun',
            id: selectedModel.id,
            name: selectedModel.name,
            project_solution_id: selectedModel.project_solution?.id,
            client_id: selectedModel.client?.id,
            dependent_driver_id: selectedModel.dependent_driver?.id,
            independent_driver_ids: selectedModel.independent_drivers?.map(
              (driver) => driver.id,
            ),
            timeframe: selectedModel.timeframe,
            parameters: selectedModel.parameters,
          }),
          signal,
        },
      )

      if (response.ok) {
        setRun(false)

        try {
          await getInitialCleanModelOutput()
        } catch (error) {
          console.error('Failed to rerun initial clean model:', error)
        }
      } else {
        console.error('Failed to run model:', response)
        setRun(false)
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        // Handle abort
      } else {
        console.error('Error running model:', error)
      }
      setRun(false)
    }
  }

  if (initialCleanModelError) {
    const isDriverChangeError = initialCleanModelError.includes('409')
    const isUnknownError =
      initialCleanModelError.includes('500') ||
      initialCleanModelError.includes('400')

    if (isDriverChangeError) {
      return (
        <ErrorContainer>
          <ErrorTitle>Please re-run this model.</ErrorTitle>
          <ErrorDescription>
            There have been changes to the model or its drivers since the last
            run. <br />
            You need to update the model for the scenario to work.
          </ErrorDescription>
          <RunButtonWithIconWrapper onClick={handleRun} disabled={run}>
            {run ? 'Loading...' : 'RUN'}
          </RunButtonWithIconWrapper>
        </ErrorContainer>
      )
    }

    if (isUnknownError) {
      return (
        <ErrorContainer>
          <ErrorDescription>
            We are unable to create this scenario because the underlying Impact
            model is out of sync. If you click “Rerun Impact model”, your model
            will be rerun and updated. <br />
            Once completed, you can continue with creating this scenario.
          </ErrorDescription>
          <RunButtonWithIconWrapper onClick={handleRun} disabled={run}>
            {run ? 'Loading...' : 'Rerun impact model'}
          </RunButtonWithIconWrapper>
        </ErrorContainer>
      )
    }

    return (
      <ErrorContainer>
        <ErrorTitle>Error Processing Scenario</ErrorTitle>
        <ErrorDescription>{initialCleanModelError}</ErrorDescription>
        <RunButtonWithIconWrapper onClick={handleRun} disabled={run}>
          {run ? 'Loading...' : 'Rerun impact model'}
        </RunButtonWithIconWrapper>
      </ErrorContainer>
    )
  }

  return (
    <PlotContainer>
      {(lastOutput?.success || initialCleanOutput?.output?.success) && (
        <XGBoostTimeSeriesChart
          output={lastOutput}
          initialCleanOutput={initialCleanOutput?.output}
          isLoading={isLoading || initialCleanOutput?.loading}
        />
      )}
    </PlotContainer>
  )
}

// XGBoost Time Series Chart Component (Chart Only)
const XGBoostTimeSeriesChart = ({ output, initialCleanOutput, isLoading }) => {
  const svgRef = useRef()
  const containerRef = useRef()
  const scenarioNames = useScenarioNameChanges() // Listen to scenario name changes
  const selectedModel = useBuildScenarioStore((state) => state.selectedModel)
  const [dimensions, setDimensions] = useState({ width: 1000, height: 450 })
  const [legendData, setLegendData] = useState([])

  // Resize observer to track container dimensions
  useEffect(() => {
    if (!containerRef.current) return

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect
        // Account for padding (6px on each side = 12px total)
        const availableWidth = Math.max(400, width - 12)
        const availableHeight = Math.max(300, height - 12)
        setDimensions({ width: availableWidth, height: availableHeight })
      }
    })

    resizeObserver.observe(containerRef.current)

    return () => {
      resizeObserver.disconnect()
    }
  }, [])

  useEffect(() => {
    // Temporary: Reset permanent scales to force recalculation
    if (scenarioPlotD3.permanentScales) {
      scenarioPlotD3.permanentScales = null // Reset to force recalculation
    }
    if (
      (!output || !output.scenarios) &&
      (!initialCleanOutput || !initialCleanOutput.scenarios)
    )
      return
    const legends = scenarioPlotD3.drawXGBoostTimeSeriesChart(
      svgRef,
      output,
      initialCleanOutput,
      scenarioNames,
      dimensions.width,
      dimensions.height,
    )
    setLegendData(legends)
  }, [output, initialCleanOutput, scenarioNames, dimensions]) // Add dimensions to dependencies

  useEffect(() => {
    scenarioPlotD3.updateLoadingIndicator(
      svgRef,
      isLoading,
      dimensions.width,
      dimensions.height,
    )
  }, [isLoading, dimensions])

  return (
    <ChartContainer ref={containerRef}>
      {selectedModel?.dependent_driver?.name && (
        <ChartTitle>
          <span>{selectedModel.dependent_driver.name}</span>
        </ChartTitle>
      )}
      <svg ref={svgRef} />
      <ChartLegend>
        {legendData.map((item, index) => (
          <LegendItem key={item.id || index} isBaseline={item.isBaseline}>
            <LegendLine color={item.color} isBaseline={item.isBaseline} />
            <span>{item.name}</span>
          </LegendItem>
        ))}
      </ChartLegend>
    </ChartContainer>
  )
}

export default BuildScenarioPlot
